import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ProcessProps {
  steps?: ProcessStep[];
  headline?: string;
  subheadline?: string;
}

const defaultSteps: ProcessStep[] = [
  {
    number: '01',
    title: 'Contact Our Team',
    description:
      'Reach out by phone at 405-408-1117 or fill out our contact form. We review your current account status and compliance situation within one business day.',
  },
  {
    number: '02',
    title: 'Account Assessment',
    description:
      'We evaluate your ISNetworld®, Veriforce, or Avetta account in full detail — identifying every gap, deficiency, and opportunity for score improvement.',
  },
  {
    number: '03',
    title: 'We Handle Everything',
    description:
      'Our experts manage documentation, safety training records, RAVS Plus requirements, and score optimization. You focus on running your business.',
  },
  {
    number: '04',
    title: '100% Compliant & Approved',
    description:
      'You receive guaranteed approval. We then maintain your account month-to-month with no long-term contracts. Cancel at any time.',
  },
];

export default function Process({
  steps = defaultSteps,
  headline = 'From Contact to Compliant',
  subheadline = 'A proven process trusted by energy contractors across the US and internationally.',
}: ProcessProps) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#0A0A0A] py-24 px-6 lg:px-14 overflow-hidden">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end">
          <div>
            <p className="overline mb-4">How It Works</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#F2EDE3] leading-tight gold-line">
              {headline}
            </h2>
          </div>
          <p className="text-[#9A9A9A] text-sm font-light leading-relaxed lg:pb-4">
            {subheadline}
          </p>
        </div>
      </div>

      {/* Steps */}
      <div
        ref={ref}
        className={`max-w-6xl mx-auto fade-up ${isVisible ? 'visible' : ''}`}
      >
        {/* Desktop connecting line */}
        <div className="hidden lg:flex items-center px-0 mb-0 relative h-0">
          <div className="absolute left-[12.5%] right-[12.5%] top-0 h-px bg-gradient-to-r from-transparent via-[#C9A030]/35 to-transparent" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-[#F2EDE3]/04">
          {steps.map((step, idx) => (
            <div
              key={idx}
              className="relative bg-[#0A0A0A] hover:bg-[#0E0E0E] transition-colors duration-500 group overflow-hidden"
              style={{ transitionDelay: `${idx * 0.12}s` }}
            >
              {/* Giant background number */}
              <div className="absolute -bottom-4 -right-2 font-serif text-[140px] lg:text-[120px] leading-none text-[#F2EDE3]/[0.025] select-none pointer-events-none font-light">
                {step.number}
              </div>

              {/* Content */}
              <div className="relative z-10 p-8 lg:p-10 flex flex-col min-h-64">
                {/* Step indicator */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-8 h-8 border border-[#C9A030]/40 flex items-center justify-center group-hover:border-[#C9A030] group-hover:bg-[#C9A030]/10 transition-all duration-300">
                    <span className="text-[#C9A030] text-[10px] font-bold tracking-wider">{step.number}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className="hidden lg:block flex-1 h-px bg-gradient-to-r from-[#C9A030]/30 to-transparent" />
                  )}
                </div>

                <div className="w-6 h-0.5 bg-[#C9A030] mb-5" />
                <h3 className="font-serif text-xl font-medium text-[#F2EDE3] mb-4 leading-snug flex-1">
                  {step.title}
                </h3>
                <p className="text-[#9A9A9A] text-sm font-light leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Embedded CTA */}
      <div
        ref={ctaRef}
        className={`max-w-6xl mx-auto mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-[#C9A030]/10 fade-up ${ctaVisible ? 'visible' : ''}`}
      >
        <div>
          <p className="text-[#F2EDE3]/70 text-base font-serif font-light">
            Ready to start? Your dedicated expert is one call away.
          </p>
          <p className="text-[#5A5A5A] text-xs font-light mt-1 tracking-wide">
            Month-to-month &middot; No contracts &middot; Guaranteed results
          </p>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0">
          <Link
            to="/contact"
            className="bg-[#C9A030] text-[#0A0A0A] px-7 py-3 text-[11px] font-bold tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:bg-[#D4B045]"
          >
            Get Started
          </Link>
          <a
            href="tel:4054081117"
            className="border border-[#F2EDE3]/18 text-[#F2EDE3]/55 px-7 py-3 text-[11px] font-medium tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:border-[#C9A030]/50 hover:text-[#C9A030]"
          >
            Call 405-408-1117
          </a>
        </div>
      </div>
    </section>
  );
}
